'use client';

import React from 'react';
import Link from 'next/link';
import CaseStudyStory from '@/components/our-work/CaseStudyStory';

export default function DigitalCircuitsPage() {
    return (
        <>
            <style>{`
                @media (max-width: 900px) {
                    .dc-card { max-width: calc(100% - 32px) !important; border-radius: 40px !important; }
                    .dc-section { padding: 48px 24px !important; }
                    .dc-images-wrap { padding: 0 24px 40px !important; }
                    .dc-two-col { flex-direction: column !important; }
                    .dc-two-col > div { width: 100% !important; height: auto !important; aspect-ratio: 4/3; }
                    .dc-phones-grid { width: 100% !important; }
                    .dc-phone-row { gap: 16px !important; }
                    .dc-phone-wrap { width: 45% !important; height: auto !important; border-radius: 32px !important; }
                    .dc-phone-wrap img { border-radius: 32px !important; }
                }
            `}</style>

            <main style={{ background: '#080400', minHeight: '100vh', paddingBottom: 80 }}>

                {/* top spacing = navbar height */}
                <div style={{ height: 112 }} />

                {/* ── White rounded card ── */}
                <div className="dc-card" style={{
                    background: '#fbfdfd',
                    borderRadius: 100,
                    width: 1798,
                    maxWidth: 'calc(100% - 122px)',
                    margin: '0 auto',
                    overflow: 'hidden',
                }}>

                    {/* ══════════════════════════════════════════════════
                        SECTION 1 — Website Development
                    ══════════════════════════════════════════════════ */}
                    <div className="dc-section" style={{
                        padding: 'clamp(48px, 7vw, 113px) clamp(24px, 6vw, 86px) 0',
                        boxSizing: 'border-box',
                    }}>

                        {/* Heading — gradient purple → black */}
                        <h1 style={{
                            fontFamily: '"Clash Display", sans-serif',
                            fontWeight: 700,
                            fontSize: 'clamp(26px, 4.5vw, 70px)',
                            letterSpacing: '0.13em',
                            background: 'linear-gradient(to right, #3730a3, #080400)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            margin: '0 0 clamp(16px, 2vw, 24px)',
                            lineHeight: 1.15,
                        }}>
                            Website Development &amp; Digital Presence Growth – Digital Circuits
                        </h1>

                        {/* Body */}
                        <p style={{
                            fontFamily: 'var(--font-manrope), Manrope, sans-serif',
                            fontWeight: 400,
                            fontSize: 'clamp(14px, 1.5vw, 24px)',
                            color: '#080400',
                            letterSpacing: '0.08em',
                            lineHeight: 1.75,
                            margin: 0,
                        }}>
                            We partnered with Digital Circuits to develop a modern, performance-focused website that strengthens their online presence and effectively communicates their expertise in electronic manufacturing and PCB solutions. The website was designed with a strong focus on user experience, mobile responsiveness, lead generation, and search engine visibility, ensuring potential customers can easily discover and engage with the brand. By creating a professional digital platform that reflects the company's technical capabilities and industry credibility, we helped establish a stronger foundation for long-term business growth, customer acquisition, and online visibility.
                        </p>
                        <CaseStudyStory
                            problem="Digital Circuits, an electronics manufacturing and PCB company, needed a website that communicated its technical credibility. On the factory floor, operators manually cross-checked component reel stickers, which caused wrong reel loads, costly rework and no traceability. Supervisors only found failures in end-of-shift audits."
                            approach={[
                                'Designed and built a mobile-first website focused on lead generation and search visibility.',
                                'Built QR Factory, a React Native app that validates reel scans in real time across three stages.',
                                'Pushed live failure alerts to the incharge dashboard over the factory LAN.',
                                'Logged every scan session to Google Sheets automatically.',
                            ]}
                            results={[
                                { num: '<1 sec', label: 'For a failed reel scan to reach the incharge dashboard, instead of surfacing in end-of-shift audits' },
                                { num: '0', label: 'Manual log entries: every scan session is recorded automatically, giving full traceability' },
                            ]}
                            services={[
                                { label: 'Website Development', href: '/services/web-development/' },
                                { label: 'UX Design', href: '/services/ux-design/' },
                                { label: 'Search Visibility', href: '/services/search-visibility/' },
                            ]}
                            theme={{ ink: '#3730a3', body: '#080400', accent: '#3730a3', panel: '#1e1b4b', panelInk: '#e0e7ff', panelAccent: '#a5b4fc' }}
                        />
                    </div>

                    {/* ── Website screenshots ── */}
                    <div className="dc-images-wrap" style={{
                        padding: 'clamp(32px, 4vw, 56px) clamp(16px, 3.5vw, 56px) clamp(32px, 4vw, 56px)',
                        boxSizing: 'border-box',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 'clamp(16px, 2vw, 32px)',
                    }}>

                        {/* Hero screenshot — full width, new digitalcircuits.in home hero (1440×900 view) */}
                        <div style={{
                            width: '100%',
                            aspectRatio: '1440 / 900',
                            overflow: 'hidden',
                            position: 'relative',
                            background: '#e8e5df',
                        }}>
                            {/* eslint-disable-next-line @next/next/no-img-element */}
                            <img
                                src="/images/our-work/digital-circuits/dc-site-hero.webp"
                                alt="Digital Circuits website home page"
                                style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center', display: 'block' }}
                            />
                        </div>

                        {/* Two side-by-side screenshots — 1344×760 and 1304×760 (same height) */}
                        <div className="dc-two-col" style={{
                            display: 'flex',
                            gap: 'clamp(12px, 1.5vw, 24px)',
                            alignItems: 'stretch',
                        }}>
                            <div style={{
                                flex: '1344',
                                aspectRatio: '1344 / 760',
                                overflow: 'hidden',
                                position: 'relative',
                                background: '#e8e5df',
                            }}>
                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                <img
                                    src="/images/our-work/digital-circuits/dc-site-services.webp"
                                    alt="Digital Circuits manufacturing services"
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center', display: 'block' }}
                                />
                            </div>
                            <div style={{
                                flex: '1304',
                                aspectRatio: '1304 / 760',
                                overflow: 'hidden',
                                position: 'relative',
                                background: '#e8e5df',
                            }}>
                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                <img
                                    src="/images/our-work/digital-circuits/dc-site-clients.webp"
                                    alt="Digital Circuits industry clients"
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center', display: 'block' }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* ══════════════════════════════════════════════════
                        SECTION 2 — QR Factory Mobile App
                    ══════════════════════════════════════════════════ */}
                    <div className="dc-section" style={{
                        padding: 'clamp(48px, 7vw, 104px) clamp(24px, 6vw, 86px) 0',
                        boxSizing: 'border-box',
                    }}>

                        {/* Heading */}
                        <h2 style={{
                            fontFamily: '"Clash Display", sans-serif',
                            fontWeight: 700,
                            fontSize: 'clamp(26px, 4.5vw, 70px)',
                            letterSpacing: '0.13em',
                            background: 'linear-gradient(to right, #3730a3, #080400)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            margin: '0 0 clamp(16px, 2vw, 24px)',
                            lineHeight: 1.15,
                        }}>
                            QR-Based Reel Verification System – Digital Circuits (Mobile Application)
                        </h2>

                        {/* Body */}
                        <p style={{
                            fontFamily: 'var(--font-manrope), Manrope, sans-serif',
                            fontWeight: 400,
                            fontSize: 'clamp(14px, 1.5vw, 24px)',
                            color: '#080400',
                            letterSpacing: '0.08em',
                            lineHeight: 1.75,
                            margin: 0,
                        }}>
                            {"Digital Circuits' assembly lines relied on operators manually cross-checking component reel stickers — a process that caused wrong reel loads, costly reworks, and zero traceability. With no real-time visibility, supervisors only discovered failures during end-of-shift audits. I built QR Factory, a React Native app that validates reel scans across three stages in real time, pushes live failure alerts to the incharge dashboard in under one second over the factory LAN, and automatically logs every session to Google Sheets — eliminating manual entry, preventing bad scans, and giving the floor complete traceability from day one."}
                        </p>
                    </div>

                    {/* ── 4 mobile mockups in 2×2 grid ── */}
                    {/* Figma: container 1221px, each phone 512×905px, borderRadius 100px, col-gap 197px, row-gap 104px */}
                    <div className="dc-phones-grid" style={{
                        width: 'clamp(300px, 70vw, 1221px)',
                        margin: 'clamp(40px, 6vw, 104px) auto clamp(60px, 7vw, 120px)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 'clamp(24px, 6vw, 104px)',
                        boxSizing: 'border-box',
                    }}>

                        {/* Row 1 */}
                        <div className="dc-phone-row" style={{
                            display: 'flex',
                            gap: 'clamp(16px, 12vw, 197px)',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                            {/* Phone 1 — dc-mobile-1.png, full cover */}
                            <div className="dc-phone-wrap" style={{
                                width: 'clamp(130px, 28vw, 512px)',
                                aspectRatio: '512 / 905',
                                borderRadius: 'clamp(24px, 5vw, 100px)',
                                overflow: 'hidden',
                                position: 'relative',
                                flexShrink: 0,
                            }}>
                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                <img
                                    src="/images/our-work/digital-circuits/dc-mobile-1.webp"
                                    alt="QR Factory operator scan"
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center', display: 'block' }}
                                />
                            </div>

                            {/* Phone 2 — dc-mobile-2.png, cropped (h=125.72%, top=-4.57%) */}
                            <div className="dc-phone-wrap" style={{
                                width: 'clamp(130px, 28vw, 512px)',
                                aspectRatio: '512 / 905',
                                borderRadius: 'clamp(24px, 5vw, 100px)',
                                overflow: 'hidden',
                                position: 'relative',
                                flexShrink: 0,
                            }}>
                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                <img
                                    src="/images/our-work/digital-circuits/dc-mobile-2.webp"
                                    alt="QR Factory incharge view"
                                    style={{
                                        width: '100%',
                                        height: '125.72%',
                                        position: 'absolute',
                                        left: 0,
                                        top: '-4.57%',
                                        maxWidth: 'none',
                                        objectFit: 'cover',
                                    }}
                                />
                            </div>
                        </div>

                        {/* Row 2 */}
                        <div className="dc-phone-row" style={{
                            display: 'flex',
                            gap: 'clamp(16px, 12vw, 197px)',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                            {/* Phone 3 — dc-mobile-3.png, full cover */}
                            <div className="dc-phone-wrap" style={{
                                width: 'clamp(130px, 28vw, 512px)',
                                aspectRatio: '512 / 905',
                                borderRadius: 'clamp(24px, 5vw, 100px)',
                                overflow: 'hidden',
                                position: 'relative',
                                flexShrink: 0,
                            }}>
                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                <img
                                    src="/images/our-work/digital-circuits/dc-mobile-3.webp"
                                    alt="QR Factory incharge access"
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center', display: 'block' }}
                                />
                            </div>

                            {/* Phone 4 — dc-mobile-4.png, cropped (h=125.72%, top=-4.46%) */}
                            <div className="dc-phone-wrap" style={{
                                width: 'clamp(130px, 28vw, 512px)',
                                aspectRatio: '512 / 905',
                                borderRadius: 'clamp(24px, 5vw, 100px)',
                                overflow: 'hidden',
                                position: 'relative',
                                flexShrink: 0,
                            }}>
                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                <img
                                    src="/images/our-work/digital-circuits/dc-mobile-4.webp"
                                    alt="QR Factory monitor"
                                    style={{
                                        width: '100%',
                                        height: '125.72%',
                                        position: 'absolute',
                                        left: 0,
                                        top: '-4.46%',
                                        maxWidth: 'none',
                                        objectFit: 'cover',
                                    }}
                                />
                            </div>
                        </div>

                    </div>

                </div>

                {/* ── Back link ── */}
                <div style={{ textAlign: 'center', marginTop: 60 }}>
                    <Link
                        href="/our-work"
                        style={{ fontFamily: 'var(--font-manrope)', fontWeight: 500, fontSize: 16, color: '#fbfdfd', opacity: 0.5, textDecoration: 'none', letterSpacing: '0.05em', transition: 'opacity 0.2s' }}
                        onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.opacity = '1'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.opacity = '0.5'; }}
                    >
                        ← Back to Our Work
                    </Link>
                </div>

            </main>
        </>
    );
}
